"use client";

import { Button } from "@/components/ui/button-pro";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { projectsData } from "@/lib/constants";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { FaGithub } from "react-icons/fa";

import { useSectionHash } from "@/hooks/use-section-hash";
import DebugInfos from "./debug-infos";
import { FuturisticLoadingSpinner } from "./loading-spinner";
import { ImagesCarouselNew } from "./projects/ImagesCarouselNew";
import { Badge } from "./ui/badge";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";

export function Projects() {
  const router = useRouter();
  const [loadingProject, setLoadingProject] = useState<{
    [key: number]: boolean;
  }>({});

  const [maxTechProjects, setMaxTechProjects] = useState<{
    [key: string]: number;
  }>(
    projectsData.reduce(
      (acc, _, index) => {
        acc[index.toString()] = 6;
        return acc;
      },
      {} as { [key: string]: number }
    )
  );

  const handleProjectClick = (index: number, projectTitle: string) => {
    if (index) {
      setLoadingProject({ [index]: true });
    }
    router.push(`/projects/${projectTitle.toLowerCase().replace(/\s+/g, "-")}`);
  };

  const handleSetProjectTechNumber = (
    projectIdx: number,
    projectTechLength: number
  ) => {
    if (maxTechProjects[projectIdx] >= projectTechLength) {
      return;
    }
    setMaxTechProjects((state) => {
      const currentProjectLength = state[projectIdx];
      return {
        ...state,
        [projectIdx]: currentProjectLength + 6,
      };
    });
  };

  // todo
  const sectionRef = useSectionHash({ hash: "#projects" });

  // Debug logging
  // console.log('Projects component rendering, projectsData length:', projectsData?.length || 0);

  return (
    <section
      ref={sectionRef}
      id="projects"
      className="pt-10 pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-purple-50 dark:from-black dark:via-gray-900 dark:to-purple-900/20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-5xl sm:text-6xl font-bold mb-8">
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 dark:from-purple-400 dark:via-pink-400 dark:to-purple-400 bg-clip-text text-transparent">
              My Projects
            </span>
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto mb-8"></div>
          <div className="text-center mb-8 p-4 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
            {/* <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"> */}
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Showcase some of my projects recently
            </p>
          </div>
        </div>

        {/* Debug info */}
        {process.env.NODE_ENV !== "production" && projectsData.length === 0 && (
          <DebugInfos data={projectsData} />
        )}

        <div className="grid md:grid-cols-2 gap-12 items-stretch">
          {projectsData.map((project, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <Card className="h-full flex flex-col bg-white/60 dark:bg-gray-800/60 border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 group overflow-hidden backdrop-blur-sm shadow-lg hover:shadow-2xl">
                <div className="relative overflow-hidden">
                  <ImagesCarouselNew
                    onClickCallback={() =>
                      handleProjectClick(index, project.title)
                    }
                    images={project.thumbnails}
                    firstThumbnail={project.highlightedThumbnail}
                  />
                  {loadingProject[index] && <FuturisticLoadingSpinner />}
                  {/* Overlay with project info */}
                  {/* */}
                  <div className="absolute inset-0  from-black/80 via-transparent to-transparent bg-gradient-to-t opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <div className="p-6 text-white">
                      <h3 className="text-2xl font-bold mb-2">
                        {project.title}
                      </h3>
                    </div>
                  </div>
                </div>

                <CardHeader className="p-6">
                  <CardTitle className="text-2xl font-bold dark:text-white group-hover:text-purple-400 transition-colors duration-300">
                    {project.title}
                  </CardTitle>
                  <CardDescription className="dark:text-gray-400 leading-relaxed text-base">
                    <ScrollArea className="h-[100px] pr-2 dark:text-gray-200 text-sm line-clamp-2">
                      {project.description}
                      <ScrollBar />
                    </ScrollArea>
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-6 pt-0 mt-auto">
                  <div className="flex flex-wrap gap-3 mb-2">
                    {project.techs
                      .slice(0, maxTechProjects[index])
                      .map((tech) => (
                        <Badge
                          key={tech.name}
                          variant="outline"
                          className="flex items-center px-4 py-2 gap-2 text-sm font-medium text-gray-700 dark:text-white bg-white/50 dark:bg-gray-700/50 hover:text-purple-400 rounded-full border border-purple-500/30 backdrop-blur-sm"
                        >
                          {tech.icon && <tech.icon className="w-5 h-5" />}
                          <span>{tech.name}</span>
                        </Badge>
                      ))}
                    {project.techs.length > (maxTechProjects[index] ?? 6) && (
                      // <Button
                      //     asChild
                      //     onClick={() =>
                      //         handleSetProjectTechNumber(
                      //             index,
                      //             project.techs.length
                      //         )
                      //     }>
                      <Badge
                        variant="outline"
                        onClick={() =>
                          handleSetProjectTechNumber(
                            index,
                            project.techs.length
                          )
                        }
                        className="text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                      >
                        +
                        {Math.max(
                          project.techs.length - (maxTechProjects[index] ?? 6),
                          0
                        )}
                      </Badge>
                    )}
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-purple-500/50   text-purple-600 dark:text-purple-300 hover:bg-purple-500 hover:text-black flex items-center gap-2 transition-all duration-300"
                      asChild
                    >
                      <Link
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FaGithub className="w-5 h-5" />
                        <span>View Code</span>
                      </Link>
                    </Button>
                    <Button
                      size="lg"
                      className="bg-gradient-to-r text-white from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl"
                      asChild
                    >
                      <Link
                        href={project.live}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="w-5 h-5" />
                        <span>Live Demo</span>
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* View All Projects Button */}
        <div className="text-center mt-16">
          <Button
            size="lg"
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            asChild
          >
            <Link href="/projects" className="flex items-center gap-2">
              View All Projects
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
